import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { ArrowLeft, Check, Plus, FileText, MessageSquare, History, ShieldAlert, Send, FileDown, Download } from "lucide-react";
import { toast } from "sonner";
import SllTargetsPanel from "./SllTargetsPanel";

const CHECK_TONE = { complete: "text-[#00E65B]", in_progress: "text-[#FFB800]", not_started: "text-muted-foreground", not_applicable: "text-muted-foreground" };

export default function FinancingCaseDetail({ caseId, refData, onBack }) {
  const [c, setC] = useState(null);
  const [terms, setTerms] = useState({});
  const [contact, setContact] = useState({});
  const [docF, setDocF] = useState({ name: "", doc_type: "" });
  const [commF, setCommF] = useState({ note: "", channel: "note", bank_request: "" });
  const [packBusy, setPackBusy] = useState(null);
  const labels = refData?.case_status_labels || {};

  const load = () => api.get(`/green-finance/cases/${caseId}`).then((r) => {
    setC(r.data);
    setTerms({
      financing_quantum_requested: r.data.financing_quantum_requested ?? "", financing_quantum_approved: r.data.financing_quantum_approved ?? "",
      indicative_interest_rate: r.data.indicative_interest_rate ?? "", final_interest_rate: r.data.final_interest_rate ?? "",
      interest_type: r.data.interest_type || "", tenure_months: r.data.tenure_months ?? "", repayment_frequency: r.data.repayment_frequency || "",
      fees: r.data.fees || "", security_or_collateral: r.data.security_or_collateral || "",
      covenants: r.data.covenants || "", sustainability_targets: r.data.sustainability_targets || "", reporting_requirements: r.data.reporting_requirements || "",
      use_of_proceeds: r.data.use_of_proceeds || "", expected_green_impact: r.data.expected_green_impact || "",
      next_action: r.data.next_action || "",
    });
    setContact({ bank_officer_name: r.data.bank_officer_name || "", bank_officer_email: r.data.bank_officer_email || "", bank_officer_phone: r.data.bank_officer_phone || "" });
  });
  useEffect(() => { load(); }, [caseId]);

  if (!c) return <div className="py-10 text-center text-muted-foreground text-sm">Loading case…</div>;

  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const setStatus = async (status) => {
    try { await api.patch(`/green-finance/cases/${caseId}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveTerms = async () => {
    try {
      await api.patch(`/green-finance/cases/${caseId}`, {
        ...terms, financing_quantum_requested: num(terms.financing_quantum_requested), financing_quantum_approved: num(terms.financing_quantum_approved),
        indicative_interest_rate: num(terms.indicative_interest_rate), final_interest_rate: num(terms.final_interest_rate), tenure_months: num(terms.tenure_months),
      });
      toast.success("Saved"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveContact = async () => { try { await api.patch(`/green-finance/cases/${caseId}`, contact); toast.success("Bank contact saved"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleCheck = async (key, status) => { try { await api.put(`/green-finance/cases/${caseId}/checklist`, { key, status }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const addDoc = async () => { if (!docF.name) return; try { await api.post(`/green-finance/cases/${caseId}/documents`, docF); setDocF({ name: "", doc_type: "" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const addComm = async () => { if (!commF.note) return; try { await api.post(`/green-finance/cases/${caseId}/communications`, commF); setCommF({ note: "", channel: "note", bank_request: "" }); load(); } catch (e) { toast.error(apiErr(e)); } };

  const downloadPack = async (fmt) => {
    setPackBusy(fmt);
    try {
      const r = await api.get(`/green-finance/cases/${caseId}/pack`, { params: { format: fmt }, responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `financing_pack.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Bank pack (${fmt.toUpperCase()}) downloaded`);
    } catch (e) { toast.error(apiErr(e)); } finally { setPackBusy(null); }
  };

  const cs = c.checklist_summary || {};
  const snap = c.linked_offer_snapshot || {};

  return (
    <div className="space-y-4" data-testid="gf-case-detail">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="case-back"><ArrowLeft className="h-4 w-4" /> Back to cases</button>

      <div className="rounded-sm border border-border bg-surface-hover/40 p-2.5 text-[11px] text-muted-foreground flex gap-2">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#FFB800]" /> This case supports preparation and tracking only. It does not represent bank approval. Pricing and terms are determined by the bank.
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold">{c.case_name}</h2>
          <div className="text-xs text-muted-foreground mt-1">{c.bank_name} · {snap.offer_name} · {c.financing_type?.replace(/_/g, " ")}</div>
          {c.linked_initiative_name && (
            <div className="text-[11px] text-accent mt-1" data-testid="case-detail-funds">
              Funds investment: {c.linked_initiative_name}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" disabled={packBusy === "pdf"} onClick={() => downloadPack("pdf")} data-testid="download-pack-pdf">
            <FileDown className="h-4 w-4" /> {packBusy === "pdf" ? "Preparing…" : "Bank pack · PDF"}
          </Button>
          <Button size="sm" variant="outline" disabled={packBusy === "docx"} onClick={() => downloadPack("docx")} data-testid="download-pack-docx">
            <Download className="h-4 w-4" /> {packBusy === "docx" ? "Preparing…" : "Word"}
          </Button>
          <Select value={c.case_status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-52" data-testid="case-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(refData?.case_statuses || []).map((s) => <SelectItem key={s} value={s}>{labels[s] || s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="summary">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="summary" data-testid="case-tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="terms" data-testid="case-tab-terms">Facility Terms</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="case-tab-checklist">Checklist & Documents</TabsTrigger>
          <TabsTrigger value="sll" data-testid="case-tab-sll">SLL Targets</TabsTrigger>
          <TabsTrigger value="comms" data-testid="case-tab-comms">Bank Communication</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="case-tab-timeline">Progress Timeline</TabsTrigger>
          <TabsTrigger value="audit" data-testid="case-tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        {/* Summary */}
        <TabsContent value="summary" className="mt-3 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Status</div><div className="text-sm font-semibold">{c.status_label}</div></div>
            <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Owner</div><div className="text-sm">{c.case_owner}</div></div>
            <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Requested quantum</div><div className="font-mono text-sm font-bold">{formatSgd(c.financing_quantum_requested)}</div></div>
            <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Approved quantum</div><div className="font-mono text-sm font-bold">{c.financing_quantum_approved != null ? formatSgd(c.financing_quantum_approved) : "—"}</div></div>
            <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Match score</div><div className="font-mono text-sm">{c.match_score != null ? `${c.match_score}/100` : "—"}</div></div>
            <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Approval likelihood</div><div className="text-sm">{c.approval_likelihood || "—"}</div></div>
            <div className="card-flat p-3 col-span-2"><div className="label-eyebrow text-[10px]">Next action</div><div className="text-sm">{c.next_action || "—"}</div></div>
          </div>
          <div className="card-flat p-3">
            <div className="label-eyebrow mb-1">Checklist readiness</div>
            <div className="flex items-center gap-3"><Progress value={cs.percent} className="h-2 flex-1" /><span className="font-mono text-sm">{cs.required_complete}/{cs.required_total} ({cs.percent}%)</span></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="card-flat p-3"><div className="label-eyebrow mb-1">Use of proceeds</div><p className="text-sm text-muted-foreground">{c.use_of_proceeds || "—"}</p></div>
            <div className="card-flat p-3"><div className="label-eyebrow mb-1">Expected green impact</div><p className="text-sm text-muted-foreground">{c.expected_green_impact || "—"}</p></div>
          </div>
        </TabsContent>

        {/* Facility terms */}
        <TabsContent value="terms" className="mt-3 space-y-3">
          <div className="card-flat p-4 grid grid-cols-2 md:grid-cols-3 gap-2">
            {[["Requested quantum (S$)", "financing_quantum_requested"], ["Approved quantum (S$)", "financing_quantum_approved"],
              ["Indicative rate (%)", "indicative_interest_rate"], ["Final rate (%)", "final_interest_rate"], ["Tenure (months)", "tenure_months"]].map(([l, k]) => (
              <div key={k}><label className="text-[10px] text-muted-foreground">{l}</label><Input type="number" value={terms[k]} onChange={(e) => setTerms({ ...terms, [k]: e.target.value })} className="h-8" data-testid={`terms-${k}`} /></div>
            ))}
            <div><label className="text-[10px] text-muted-foreground">Interest type</label>
              <Select value={terms.interest_type || "unknown"} onValueChange={(v) => setTerms({ ...terms, interest_type: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{["fixed", "floating", "unknown"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-[10px] text-muted-foreground">Repayment frequency</label>
              <Select value={terms.repayment_frequency || "monthly"} onValueChange={(v) => setTerms({ ...terms, repayment_frequency: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{(refData?.repayment_frequencies || []).map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-[10px] text-muted-foreground">Fees</label><Input value={terms.fees} onChange={(e) => setTerms({ ...terms, fees: e.target.value })} className="h-8" /></div>
          </div>
          <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {[["Security / collateral", "security_or_collateral"], ["Covenants", "covenants"], ["Sustainability performance targets", "sustainability_targets"], ["Reporting requirements", "reporting_requirements"], ["Next action", "next_action"]].map(([l, k]) => (
              <div key={k}><label className="text-[10px] text-muted-foreground">{l}</label><Input value={terms[k]} onChange={(e) => setTerms({ ...terms, [k]: e.target.value })} className="h-8" /></div>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground">Snapshot offer rate source: {snap.interest_rate_source || "—"}. {snap.interest_rate_source === "unknown" && "Rate not published — enter the bank's quoted rate above."}</div>
          <div className="flex justify-end"><Button variant="primary" size="sm" onClick={saveTerms} data-testid="save-terms-btn"><Check className="h-4 w-4" /> Save terms</Button></div>
        </TabsContent>

        {/* Checklist & documents */}
        <TabsContent value="checklist" className="mt-3 space-y-3">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2">Application checklist {cs.all_required_complete ? <Badge variant="success" className="ml-2 text-[9px]">Ready to submit</Badge> : null}</div>
            <div className="space-y-1.5">
              {(c.checklist || []).map((it) => (
                <div key={it.key} className="flex items-center gap-2 text-sm" data-testid={`checklist-${it.key}`}>
                  <span className="flex-1">{it.label} {it.required && <span className="text-[9px] text-muted-foreground">(required)</span>}</span>
                  <Select value={it.status} onValueChange={(v) => toggleCheck(it.key, v)}>
                    <SelectTrigger className={`h-7 w-36 text-xs ${CHECK_TONE[it.status]}`} data-testid={`checklist-select-${it.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["not_started", "in_progress", "complete", "not_applicable"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Documents</div>
            <div className="space-y-1">
              {(c.documents || []).map((d) => <div key={d.id} className="text-sm flex justify-between border-b border-border py-1"><span>{d.name} {d.doc_type && <span className="text-[10px] text-muted-foreground">· {d.doc_type}</span>}</span><span className="text-[10px] text-muted-foreground">{new Date(d.uploaded_at).toLocaleDateString()}</span></div>)}
              {!(c.documents || []).length && <div className="text-xs text-muted-foreground">No documents logged.</div>}
            </div>
            <div className="flex gap-2 mt-2">
              <Input placeholder="Document name" value={docF.name} onChange={(e) => setDocF({ ...docF, name: e.target.value })} className="h-8" data-testid="doc-name" />
              <Input placeholder="Type (optional)" value={docF.doc_type} onChange={(e) => setDocF({ ...docF, doc_type: e.target.value })} className="h-8 w-40" />
              <Button size="sm" variant="outline" onClick={addDoc} data-testid="add-doc-btn"><Plus className="h-3.5 w-3.5" /> Add</Button>
            </div>
          </div>
        </TabsContent>

        {/* SLL Targets */}
        <TabsContent value="sll" className="mt-3">
          <SllTargetsPanel caseId={caseId} />
        </TabsContent>

        {/* Bank communication */}
        <TabsContent value="comms" className="mt-3 space-y-3">
          <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div><label className="text-[10px] text-muted-foreground">Bank officer</label><Input value={contact.bank_officer_name} onChange={(e) => setContact({ ...contact, bank_officer_name: e.target.value })} className="h-8" data-testid="officer-name" /></div>
            <div><label className="text-[10px] text-muted-foreground">Email</label><Input value={contact.bank_officer_email} onChange={(e) => setContact({ ...contact, bank_officer_email: e.target.value })} className="h-8" /></div>
            <div><label className="text-[10px] text-muted-foreground">Phone</label><Input value={contact.bank_officer_phone} onChange={(e) => setContact({ ...contact, bank_officer_phone: e.target.value })} className="h-8" /></div>
            <div className="md:col-span-3 flex justify-end"><Button size="sm" variant="outline" onClick={saveContact} data-testid="save-contact-btn">Save contact</Button></div>
          </div>
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> Communication log</div>
            <div className="space-y-2">
              {(c.communications || []).map((m) => (
                <div key={m.id} className="text-sm border-l-2 border-accent/50 pl-2"><div>{m.note}</div>{m.bank_request && <div className="text-[11px] text-[#FFB800]">Bank request: {m.bank_request}</div>}<div className="text-[10px] text-muted-foreground">{m.channel} · {m.by} · {new Date(m.at).toLocaleString()}</div></div>
              ))}
              {!(c.communications || []).length && <div className="text-xs text-muted-foreground">No communications logged.</div>}
            </div>
            <div className="mt-2 space-y-2">
              <Textarea placeholder="Log a call / email / meeting note…" value={commF.note} onChange={(e) => setCommF({ ...commF, note: e.target.value })} className="min-h-[60px]" data-testid="comm-note" />
              <div className="flex gap-2">
                <Select value={commF.channel} onValueChange={(v) => setCommF({ ...commF, channel: v })}><SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>{["note", "call", "email", "meeting"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                <Input placeholder="Bank request (optional)" value={commF.bank_request} onChange={(e) => setCommF({ ...commF, bank_request: e.target.value })} className="h-8 flex-1" />
                <Button size="sm" variant="primary" onClick={addComm} data-testid="add-comm-btn"><Send className="h-3.5 w-3.5" /> Log</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline" className="mt-3">
          <div className="card-flat p-4">
            <div className="space-y-2">
              {(c.timeline || []).slice().reverse().map((t, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm"><span className="h-2 w-2 rounded-full bg-accent" /><span className="flex-1">{t.event}</span><span className="text-[10px] text-muted-foreground">{new Date(t.at).toLocaleString()}</span></div>
              ))}
              {!(c.timeline || []).length && <div className="text-xs text-muted-foreground">No timeline events.</div>}
            </div>
          </div>
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit" className="mt-3">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> Audit trail</div>
            <div className="space-y-2">
              {(c.audit_trail || []).slice().reverse().map((a) => (
                <div key={a.id} className="text-sm border-b border-border pb-1.5"><div className="flex justify-between"><span className="font-medium">{a.action}</span><span className="text-[10px] text-muted-foreground">{new Date(a.at).toLocaleString()}</span></div>{a.detail && <div className="text-[11px] text-muted-foreground">{a.detail}{a.from_status ? ` (${a.from_status} → ${a.to_status})` : ""}</div>}<div className="text-[10px] text-muted-foreground">{a.user} · {a.role}</div></div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
